import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { apiRoute } from '../../../constants/api-constants';
import { ROLES, roleLabel } from '../../../auth/roles';
import { useAuth } from '../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../auth/permissions';
import {
  getPortfolio,
  listPortfolioBuildings,
  listPortfolios,
  listUnits,
} from '../../../api/hierarchy-client';
import {
  Portfolio,
  UnitListItem,
} from '../../../interfaces/hierarchy-interfaces';
import { InviteQrModal } from './invite-qr-modal';
import { ActionWithReason } from '../../pages/dashboard/dashboard-shared-components/disabled-reason';
import { StatusLabel } from '../../../motion';
import {
  Field,
  FieldRow,
  Label,
  Input,
  Select,
  PrimaryButton,
  ButtonRow,
  FieldError,
  HelperText,
} from '../onboarding.styled';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface BuildingOption {
  id: number;
  name: string;
}

interface Props {
  /** Rendered at the right end of the Send-invite row — the wizard's own
   *  "Continue". Optional: the dashboard's copies of this form have nowhere to
   *  continue to. */
  trailingAction?: React.ReactNode;
  // Roles this inviter may pick. If one, the selector is hidden.
  allowedRoles: string[];
  // Required when resident is an option (residents must be bound to a building).
  buildings?: BuildingOption[];
  onCreated?: () => void;
}

// A unit whose kind is still 'unassigned' is a migration placeholder standing in
// for a resident who had no unit number — a real, selectable unit that needs
// attention, so it is labelled rather than hidden.
const unitOptionLabel = (unit: UnitListItem): string => {
  if (unit.kind === 'unassigned') {
    return `${unit.unit_number} · needs assignment`;
  }

  return unit.is_vacant ? unit.unit_number : `${unit.unit_number} · occupied`;
};

export const InviteForm: React.FC<Props> = ({
  trailingAction,
  allowedRoles,
  buildings = [],
  onCreated,
}) => {
  const { permissions }: { permissions: string[] } = useAuth();
  const canReadPortfolio = can(permissions, PERMISSIONS.PORTFOLIO_READ);
  const canReadUnit = can(permissions, PERMISSIONS.UNIT_READ);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: allowedRoles[0] || ROLES.CONCIERGE,
    portfolio_id: '',
    building_id: buildings[0]?.id ? String(buildings[0].id) : '',
    unit_id: '',
    unit_number: '',
  });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const submitRef = useRef(false);
  const [modal, setModal] = useState<{
    open: boolean;
    inviteUrl: string;
    qrDataUrl: string;
    emailSent: boolean;
    name: string;
  }>({ open: false, inviteUrl: '', qrDataUrl: '', emailSent: false, name: '' });

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  // Which portfolio holds which building, for the WHOLE org, in one request.
  // Without it the building select is a flat list and the hierarchy the account
  // is organised around is invisible at exactly the moment somebody is being
  // placed inside it.
  const [buildingPortfolio, setBuildingPortfolio] = useState<
    Map<number, string>
  >(new Map());
  // null = no portfolio filter applied (show every building the inviter has).
  // An empty array is meaningfully different: a portfolio holding no buildings.
  const [portfolioBuildingIds, setPortfolioBuildingIds] = useState<
    number[] | null
  >(null);
  const [units, setUnits] = useState<UnitListItem[]>([]);
  // Set when the unit list could not be read. The free-text unit box comes back
  // in that case, so losing the picker never blocks an invite.
  const [unitsUnavailable, setUnitsUnavailable] = useState(false);
  // "Still fetching" is not "there are none". Without this the fallback box
  // flashed in for the length of the request on every building change.
  const [unitsLoading, setUnitsLoading] = useState(false);

  const isResident = form.role === ROLES.RESIDENT;

  // Portfolios are a filter over the buildings the parent already handed us —
  // they never widen the set, so a failure here silently leaves the plain
  // building list rather than blocking the form.
  useEffect(() => {
    if (!canReadPortfolio) {
      return undefined;
    }

    let cancelled = false;

    listPortfolios()
      .then(rows => {
        if (!cancelled) {
          setPortfolios(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPortfolios([]);
        }
      });

    // ONE request for the whole index — GET /portfolios/buildings exists for
    // exactly this and replaces a getPortfolio() per portfolio. A failure is
    // silent on purpose: losing the grouping leaves a flat building list, which
    // is what this screen had before, and is never a reason to block an invite.
    listPortfolioBuildings()
      .then(rows => {
        if (cancelled) return;
        setBuildingPortfolio(
          new Map(
            rows.map(row => [row.id, row.portfolio_name || 'No portfolio'])
          )
        );
      })
      .catch(() => {
        if (!cancelled) setBuildingPortfolio(new Map());
      });

    return () => {
      cancelled = true;
    };
  }, [canReadPortfolio]);

  // Which buildings the chosen portfolio contains. GET /portfolios/:id is the
  // only place that mapping is exposed — the legacy building payload the parent
  // passes down carries no portfolio_id.
  useEffect(() => {
    if (!form.portfolio_id) {
      setPortfolioBuildingIds(null);

      return undefined;
    }

    let cancelled = false;

    getPortfolio(Number(form.portfolio_id))
      .then(detail => {
        if (!cancelled) {
          setPortfolioBuildingIds(detail.buildings.map(b => b.id));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPortfolioBuildingIds(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form.portfolio_id]);

  const visibleBuildings = useMemo(() => {
    if (portfolioBuildingIds === null) {
      return buildings;
    }

    return buildings.filter(b => portfolioBuildingIds.includes(b.id));
  }, [buildings, portfolioBuildingIds]);

  // Buildings often load AFTER mount (parent fetches post-render), so the
  // useState initializer misses them — pre-select the first once they arrive.
  // The same effect repairs a selection the portfolio filter just excluded,
  // which is what keeps the cascade from sitting on an invisible building.
  useEffect(() => {
    const stillVisible = visibleBuildings.some(
      b => String(b.id) === form.building_id
    );

    if (stillVisible) {
      return;
    }

    const next = visibleBuildings[0]?.id ? String(visibleBuildings[0].id) : '';

    if (next !== form.building_id) {
      setForm(f => ({ ...f, building_id: next, unit_id: '' }));
    }
  }, [visibleBuildings, form.building_id]);

  // Units hang off a building, so this only runs once one is chosen, and only
  // for a resident — a staff invite is scoped to buildings, never to a door.
  useEffect(() => {
    if (!canReadUnit || !isResident || !form.building_id) {
      setUnits([]);
      setUnitsUnavailable(false);
      setUnitsLoading(false);

      return undefined;
    }

    let cancelled = false;

    setUnitsUnavailable(false);
    setUnitsLoading(true);
    listUnits(Number(form.building_id))
      .then(rows => {
        if (!cancelled) {
          setUnits(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUnits([]);
          setUnitsUnavailable(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setUnitsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canReadUnit, isResident, form.building_id]);

  // THREE DIFFERENT ANSWERS THAT ALL LOOKED THE SAME.
  //
  // The unit control used to fall back to a free-text box whenever `units` was
  // empty, whatever the reason — still loading, this building genuinely has no
  // units, or the request failed. So a resident invite during onboarding showed
  // a blank "Unit" field with no explanation, and the natural reading was that
  // the building/unit relationship had been ignored. They are separate states
  // now, and each says which one it is.
  const showUnitSelect = isResident && !unitsUnavailable && units.length > 0;
  const unitsEmpty =
    isResident &&
    !unitsLoading &&
    !unitsUnavailable &&
    !!form.building_id &&
    units.length === 0;

  const set =
    (name: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [name]: e.target.value }));

  // Changing the portfolio invalidates the building beneath it, and changing the
  // building invalidates the unit beneath that. Clearing the child here rather
  // than letting it linger stops a stale unit_id from being posted against a
  // building it does not belong to (a 400 the server would rightly refuse).
  const setPortfolio = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setForm(f => ({ ...f, portfolio_id: e.target.value, unit_id: '' }));

  const setBuilding = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setForm(f => ({ ...f, building_id: e.target.value, unit_id: '' }));

  /* WP-32 / MR-22 — ONE DEFINITION OF "NOT READY YET".
   *
   * This used to exist only inside `validate()`, which runs on SUBMIT. The CTA
   * was `disabled={submitting}` — in-flight state and nothing else — so an empty
   * form offered a live "Send invite" button, and pressing it was the only way
   * to find out it was not ready. On THIS form that is worse than on most: the
   * happy path fires a real outbound email, so "press it and see" is not a free
   * experiment.
   *
   * Extracted so the GATE and the SUBMIT CHECK cannot drift apart. Two
   * implementations of "required" is how a button comes to be disabled for a
   * reason the form never shows, or enabled into a refusal. */
  const missingFields = (): { [k: string]: string } => {
    const next: { [k: string]: string } = {};
    if (!form.first_name.trim()) next.first_name = 'Required';
    if (!form.last_name.trim()) next.last_name = 'Required';
    if (!EMAIL_RE.test(form.email)) next.email = 'Enter a valid email';
    // Require a building whenever a picker is shown (residents always; staff too
    // when the inviter has buildings) — otherwise the invitee lands with none.
    // Read off the FILTERED list: a portfolio with no buildings shows no options,
    // and demanding a choice there would be a dead end.
    if (visibleBuildings.length && !form.building_id)
      next.building_id = 'Pick a building';

    return next;
  };

  const validate = () => {
    const next = missingFields();
    setErrors(next);

    return Object.keys(next).length === 0;
  };

  /* WHAT IS OUTSTANDING, IN WORDS, FOR THE DISABLED CONTROL.
   *
   * A disabled button with no reason is a broken button — the argument
   * disabled-reason.tsx was written for (WP-43), and replacing
   * "enabled, then refused" with "disabled, and silent" would be the same defect
   * wearing different clothes.
   *
   * The EMAIL is described as invalid rather than missing only once something
   * has been typed: "Enter a valid email address" on an untouched form reads as
   * a complaint about work not yet done. */
  const outstanding = (): string | null => {
    const missing = missingFields();
    const parts: string[] = [];
    if (missing.first_name || missing.last_name) parts.push('their name');
    if (missing.email) {
      parts.push(
        form.email.trim() ? 'a valid email address' : 'an email address'
      );
    }
    if (missing.building_id) parts.push('a building');
    if (!parts.length) return null;

    const list =
      parts.length === 1
        ? parts[0]
        : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

    return `Add ${list} to send this invitation.`;
  };

  const submit = async () => {
    if (submitRef.current) return;
    if (!validate()) return;

    submitRef.current = true;
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        role: form.role,
      };
      if (form.role === ROLES.RESIDENT) {
        body.building_id = Number(form.building_id);
        // A picked unit is the most specific placement there is and the server
        // scopes the invite to it; the free-text number stays the fallback for a
        // building whose units are not on file yet.
        if (form.unit_id) {
          body.unit_id = Number(form.unit_id);
        } else {
          body.unit_number = form.unit_number.trim();
        }
      } else if (form.building_id) {
        body.property_ids = [Number(form.building_id)];
      }
      // Sent for staff so an invite with no building still lands on a scope.
      // With a building chosen the server prefers the narrower building scope,
      // so this is additive, never a downgrade.
      if (form.role !== ROLES.RESIDENT && form.portfolio_id) {
        body.portfolio_id = Number(form.portfolio_id);
      }
      const res = await axios.post(`${apiRoute}/dashboard/invitations`, body);
      setModal({
        open: true,
        inviteUrl: res.data.invite_url,
        qrDataUrl: res.data.qr_data_url,
        emailSent: !!res.data.email_sent,
        name: `${form.first_name} ${form.last_name}`.trim(),
      });
      setForm(f => ({
        ...f,
        first_name: '',
        last_name: '',
        email: '',
        unit_id: '',
        unit_number: '',
      }));
      onCreated?.();
    } catch (error) {
      const ax = error as AxiosError<{ message?: string }>;
      setErrors({
        _form:
          ax.response?.data?.message ||
          'Could not send the invitation. Please try again.',
      });
    } finally {
      submitRef.current = false;
      setSubmitting(false);
    }
  };

  const showRoleSelect = allowedRoles.length > 1;
  const showPortfolioSelect = buildings.length > 0 && portfolios.length > 0;

  // THE HIERARCHY, MADE VISIBLE.
  //
  // The complaint this answers: "portfolios, buildings and units are connected
  // and it is not respected here". The FILTER always worked — choose a portfolio
  // and the list narrows to it — but with the default "All portfolios" the
  // buildings arrived as one flat list with nothing to say which portfolio each
  // one belonged to, so an account with three portfolios and one building looked
  // like a screen that had ignored the structure entirely.
  //
  // Grouped only when the filter is OFF. Inside a chosen portfolio every option
  // has the same heading, and a one-group <optgroup> is a box drawn around a
  // fact the select above already states.
  const groupedBuildings = useMemo(() => {
    const groups = new Map<string, BuildingOption[]>();
    visibleBuildings.forEach(b => {
      const key = buildingPortfolio.get(b.id) || 'No portfolio';
      if (!groups.has(key)) groups.set(key, []);
      (groups.get(key) as BuildingOption[]).push(b);
    });

    // "No portfolio" last: it is the exception, and floating it to the top
    // because of where it happens to sort would bury the real ones.
    return [...groups.entries()].sort(([a], [b]) =>
      a === 'No portfolio' ? 1 : b === 'No portfolio' ? -1 : a.localeCompare(b)
    );
  }, [visibleBuildings, buildingPortfolio]);

  const showBuildingGroups =
    !form.portfolio_id &&
    buildingPortfolio.size > 0 &&
    groupedBuildings.length > 1;

  const buildingSelect = (
    <>
      <Label htmlFor="invite-building-id">Building</Label>
      <Select
        id="invite-building-id"
        name="building_id"
        value={form.building_id}
        onChange={setBuilding}
      >
        <option value="">Select a building…</option>
        {showBuildingGroups
          ? groupedBuildings.map(([portfolioName, rows]) => (
              <optgroup key={portfolioName} label={portfolioName}>
                {rows.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </optgroup>
            ))
          : visibleBuildings.map(b => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
      </Select>
      <FieldError message={errors.building_id} />
      {portfolioBuildingIds !== null && visibleBuildings.length === 0 && (
        <HelperText>
          This portfolio has no buildings yet. Pick another portfolio, or choose
          all portfolios.
        </HelperText>
      )}
    </>
  );

  return (
    <div>
      <FieldRow>
        <Field>
          <Label htmlFor="invite-first-name">First name</Label>
          <Input
            id="invite-first-name"
            name="first_name"
            value={form.first_name}
            onChange={set('first_name')}
            hasError={!!errors.first_name}
            placeholder="Jordan"
          />
          <FieldError message={errors.first_name} />
        </Field>
        <Field>
          <Label htmlFor="invite-last-name">Last name</Label>
          <Input
            id="invite-last-name"
            name="last_name"
            value={form.last_name}
            onChange={set('last_name')}
            hasError={!!errors.last_name}
            placeholder="Rivera"
          />
          <FieldError message={errors.last_name} />
        </Field>
      </FieldRow>

      <Field>
        <Label htmlFor="invite-email">Email</Label>
        <Input
          type="email"
          id="invite-email"
          name="email"
          value={form.email}
          onChange={set('email')}
          hasError={!!errors.email}
          placeholder="jordan@building.com"
        />
        <FieldError message={errors.email} />
      </Field>

      {showRoleSelect && (
        <Field>
          <Label htmlFor="invite-role">Role</Label>
          <Select
            id="invite-role"
            name="role"
            value={form.role}
            onChange={set('role')}
          >
            {allowedRoles.map(r => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {showPortfolioSelect && (
        <Field>
          <Label htmlFor="invite-portfolio-id">Portfolio</Label>
          <Select
            id="invite-portfolio-id"
            name="portfolio_id"
            value={form.portfolio_id}
            onChange={setPortfolio}
          >
            <option value="">All portfolios</option>
            {/* THE COUNT IS IN THE LABEL, and it is the whole fix for "the
                hierarchy is not respected here". The cascade always worked —
                pick a portfolio and the building list narrows to it — but a
                portfolio holding NO buildings looked identical to a broken
                filter: you chose it, the building list emptied, and nothing on
                screen said why until after you had committed to the choice.
                Stating it up front turns an apparent bug into a fact about the
                data. */}
            {portfolios.map(p => (
              <option key={p.id} value={p.id}>
                {p.building_count === 0
                  ? `${p.name} · no buildings yet`
                  : p.building_count === 1
                    ? `${p.name} · 1 building`
                    : `${p.name} · ${p.building_count} buildings`}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {buildings.length > 0 && isResident && (
        <>
          <FieldRow>
            <Field>{buildingSelect}</Field>
            {showUnitSelect ? (
              <Field>
                <Label htmlFor="invite-unit-id">Unit</Label>
                <Select
                  id="invite-unit-id"
                  name="unit_id"
                  value={form.unit_id}
                  onChange={set('unit_id')}
                >
                  <option value="">Select a unit…</option>
                  {units.map(u => (
                    <option key={u.id} value={u.id}>
                      {unitOptionLabel(u)}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              <Field>
                <Label htmlFor="invite-unit-number">Unit (optional)</Label>
                <Input
                  id="invite-unit-number"
                  value={form.unit_number}
                  onChange={set('unit_number')}
                  placeholder="4B"
                />
              </Field>
            )}
          </FieldRow>
          {isResident && !showUnitSelect && form.building_id && (
            <HelperText>
              {unitsLoading
                ? 'Looking up the units in this building…'
                : unitsUnavailable
                  ? 'Units could not be loaded for this building — type the unit number instead.'
                  : unitsEmpty
                    ? 'This building has no units on file yet. Add them from Dashboard → Buildings and the list will fill in — for now, type the unit number and it can be assigned later.'
                    : 'Type the unit number and it can be assigned later.'}
            </HelperText>
          )}
        </>
      )}

      {buildings.length > 0 && !isResident && <Field>{buildingSelect}</Field>}

      <FieldError message={errors._form} />

      {/* SEND INVITE ON THE LEFT, the step's own forward action on the right.
          `trailingAction` rather than a second row: the two belong on one line
          and the wizard is the only thing that knows what "forward" means from
          here. Without one the row keeps its old shape. */}
      <ButtonRow>
        <ActionWithReason reason={submitting ? null : outstanding()}>
          {describedBy => (
            <PrimaryButton
              type="button"
              onClick={submit}
              disabled={submitting || Boolean(outstanding())}
              aria-describedby={describedBy}
            >
              <StatusLabel
                state={submitting ? 'busy' : 'idle'}
                labels={{ idle: 'Send invite', busy: 'Sending…' }}
              />
            </PrimaryButton>
          )}
        </ActionWithReason>
        {trailingAction || <span />}
      </ButtonRow>

      <InviteQrModal
        open={modal.open}
        onClose={() => setModal(m => ({ ...m, open: false }))}
        inviteeName={modal.name}
        inviteUrl={modal.inviteUrl}
        qrDataUrl={modal.qrDataUrl}
        emailSent={modal.emailSent}
      />
    </div>
  );
};
